import type { Editor } from '@tiptap/core'

// AST Node Types
interface MarkdownNode {
  type: string
  content?: string
  children?: MarkdownNode[]
  level?: number
  ordered?: boolean
  checked?: boolean
  marks?: any[]
}

interface InlineToken {
  type: 'text' | 'bold' | 'italic' | 'code' | 'strike' | 'link'
  content: string
  marks?: any[]
  href?: string
  children?: InlineToken[]
}

export class MarkdownRenderer {
  private editor: Editor

  constructor(editor: Editor) {
    this.editor = editor
  }

  // Main entry point for rendering markdown content
  renderMarkdown(tr: any, startPos: number, text: string): number {
    // Handle empty or whitespace-only input
    if (!text || text.trim() === '') {
      const emptyParagraph = this.editor.schema.nodes.paragraph.create()
      tr.replaceWith(startPos, startPos, [emptyParagraph])
      return startPos + 1
    }
    
    const ast = this.parseMarkdownToAST(text)
    const nodes = this.createNodesFromAST(ast)
    
    // Ensure we always have at least one node
    if (nodes.length === 0) {
      const emptyParagraph = this.editor.schema.nodes.paragraph.create()
      nodes.push(emptyParagraph)
    }
    
    return this.insertNodes(tr, startPos, nodes)
  }

  // Parse markdown text into AST
  private parseMarkdownToAST(text: string): MarkdownNode[] {
    const lines = text.split('\n')
    return this.parseBlocksRecursive(lines, 0, lines.length)
  }

  // Recursively parse blocks with proper nesting
  private parseBlocksRecursive(lines: string[], start: number, end: number): MarkdownNode[] {
    const nodes: MarkdownNode[] = []
    let i = start

    while (i < end) {
      const line = lines[i]
      const trimmedLine = line.trim()

      // Skip empty lines between blocks
      if (trimmedLine === '') {
        i++
        continue
      }

      // Check for horizontal rule
      if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmedLine)) {
        nodes.push({ type: 'horizontalRule' })
        i++
        continue
      }

      // Check for heading
      const headingMatch = line.match(/^(#{1,6})\s+(.*)/)
      if (headingMatch) {
        nodes.push({
          type: 'heading',
          level: headingMatch[1].length,
          content: headingMatch[2]
        })
        i++
        continue
      }

      // Check for blockquote
      if (line.startsWith('>')) {
        const blockquoteLines: string[] = []
        let j = i
        
        while (j < end && (lines[j].startsWith('>') || lines[j].trim() === '')) {
          const quoteLine = lines[j].startsWith('>') 
            ? lines[j].substring(1).trimStart()
            : lines[j]
          blockquoteLines.push(quoteLine)
          j++
        }
        
        // Recursively parse blockquote content
        const blockquoteAst = this.parseBlocksRecursive(blockquoteLines, 0, blockquoteLines.length)
        nodes.push({
          type: 'blockquote',
          children: blockquoteAst
        })
        
        i = j
        continue
      }

      // Check for unordered list
      if (line.match(/^\s*[-*+]\s+/)) {
        const listItems: MarkdownNode[] = []
        let j = i
        
        while (j < end && lines[j].match(/^\s*[-*+]\s+/)) {
          const itemContent = lines[j].replace(/^\s*[-*+]\s+/, '')
          listItems.push({
            type: 'listItem',
            content: itemContent
          })
          j++
        }
        
        nodes.push({
          type: 'bulletList',
          children: listItems
        })
        
        i = j
        continue
      }

      // Check for ordered list
      if (line.match(/^\s*\d+\.\s+/)) {
        const listItems: MarkdownNode[] = []
        let j = i
        
        while (j < end && lines[j].match(/^\s*\d+\.\s+/)) {
          const itemContent = lines[j].replace(/^\s*\d+\.\s+/, '')
          listItems.push({
            type: 'listItem',
            content: itemContent
          })
          j++
        }
        
        nodes.push({
          type: 'orderedList',
          ordered: true,
          children: listItems
        })
        
        i = j
        continue
      }

      // Check for code block
      if (line.startsWith('```')) {
        const codeLines: string[] = []
        let j = i + 1

        while (j < end && !lines[j].startsWith('```')) {
          codeLines.push(lines[j])
          j++
        }

        nodes.push({
          type: 'codeBlock',
          content: codeLines.join('\n')
        })

        i = j + 1
        continue
      }

      // Check for image
      const imageMatch = trimmedLine.match(/^!\[([^\]]*)\]\(([^)]+)\)/)
      if (imageMatch) {
        let imageUrl = imageMatch[2]

        // Check if we're in browser (not Electron)
        const isElectron = typeof window !== 'undefined' &&
                          ((window as any).electronAPI || (window as any).api)

        // Only convert relative paths to Vite import paths in browser/demo mode
        // In Electron, keep paths as-is (they will be resolved relative to vault)
        if (!isElectron) {
          if (imageUrl.startsWith('./')) {
            imageUrl = imageUrl.replace(/^\.\//, '/src/lib/templates/')
          } else if (imageUrl.startsWith('../')) {
            imageUrl = imageUrl.replace(/^\.\.\//, '/src/lib/templates/')
          }
        }

        nodes.push({
          type: 'image',
          content: imageUrl
        })

        i++
        continue
      }

      // Default to paragraph
      const paragraphLines: string[] = []
      let j = i
      
      while (j < end && lines[j].trim() !== '' && 
             !lines[j].startsWith('#') && 
             !lines[j].startsWith('>') &&
             !lines[j].match(/^\s*[-*+]\s+/) &&
             !lines[j].match(/^\s*\d+\.\s+/) &&
             !lines[j].startsWith('```') &&
             !/^(-{3,}|\*{3,}|_{3,})$/.test(lines[j].trim())) {
        paragraphLines.push(lines[j])
        j++
      }
      
      if (paragraphLines.length > 0) {
        nodes.push({
          type: 'paragraph',
          content: paragraphLines.join('\n')
        })
      }
      
      i = j
    }

    return nodes
  }

  // Create ProseMirror nodes from AST
  private createNodesFromAST(ast: MarkdownNode[]): any[] {
    const nodes: any[] = []
    for (const astNode of ast) {
      const node = this.createNodeFromAST(astNode)
      if (node !== null) {
        // Handle case where createNodeFromAST returns an array
        if (Array.isArray(node)) {
          nodes.push(...node)
        } else {
          nodes.push(node)
        }
      }
    }
    return nodes
  }

  // Check if a URL is a YouTube URL and extract video ID
  private isYouTubeUrl(url: string): string | null {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/,
      /^https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
      /^https?:\/\/youtu\.be\/([a-zA-Z0-9_-]+)/
    ]

    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match && match[1]) {
        return match[1]
      }
    }
    return null
  }

  private createNodeFromAST(astNode: MarkdownNode): any {
    switch (astNode.type) {
      case 'horizontalRule':
        return this.editor.schema.nodes.horizontalRule.create()

      case 'heading':
        return this.createHeading(astNode.level || 1, astNode.content || '')

      case 'blockquote':
        return this.createBlockquote(astNode.children || [])

      case 'bulletList':
        return this.createBulletList(astNode.children || [])

      case 'orderedList':
        return this.createOrderedList(astNode.children || [])

      case 'codeBlock':
        return this.createCodeBlock(astNode.content || '')

      case 'image':
        return this.createImage(astNode.content || '')

      case 'paragraph':
        return this.createParagraph(astNode.content || '')

      default:
        return null
    }
  }

  private createImage(src: string): any {
    // TipTap image nodes are inline nodes by default
    if (this.editor.schema.nodes.image) {
      const imageNode = this.editor.schema.nodes.image.create({ src })
      // Wrap in paragraph for block-level display
      return this.editor.schema.nodes.paragraph.create({}, [imageNode])
    }
    // Fallback: create a paragraph with the image markdown
    return this.createParagraph(`![image](${src})`)
  }

  private createHeading(level: number, content: string): any {
    const inlineNodes = this.parseAndCreateInlineNodes(content)
    return this.editor.schema.nodes.heading.create(
      { level },
      inlineNodes.length > 0 ? inlineNodes : null
    )
  }

  private createBlockquote(children: MarkdownNode[]): any {
    // Recursively create nodes from children
    const childNodes = this.createNodesFromAST(children)
    
    // If no children, create empty paragraph
    if (childNodes.length === 0) {
      const emptyPara = this.editor.schema.nodes.paragraph.create()
      return this.editor.schema.nodes.blockquote.create({}, emptyPara)
    }
    
    // TipTap blockquotes typically contain a single element
    if (childNodes.length === 1) {
      return this.editor.schema.nodes.blockquote.create({}, childNodes[0])
    }
    
    // For multiple children, try to merge paragraphs if possible
    const allParagraphs = childNodes.every(node => 
      node.type === this.editor.schema.nodes.paragraph
    )
    
    if (allParagraphs && childNodes.length > 1) {
      // Merge multiple paragraphs with hard breaks
      const mergedContent: any[] = []
      for (let i = 0; i < childNodes.length; i++) {
        if (i > 0) {
          mergedContent.push(this.editor.schema.nodes.hardBreak.create())
          mergedContent.push(this.editor.schema.nodes.hardBreak.create())
        }
        const para = childNodes[i]
        if (para.content && para.content.length) {
          mergedContent.push(...para.content)
        }
      }
      const mergedPara = this.editor.schema.nodes.paragraph.create({}, mergedContent)
      return this.editor.schema.nodes.blockquote.create({}, mergedPara)
    }
    
    // For mixed content, return as array
    return childNodes.map(child => 
      this.editor.schema.nodes.blockquote.create({}, child)
    )
  }

  private createBulletList(items: MarkdownNode[]): any {
    const listItems = items.map(item => this.createListItem(item))
    return this.editor.schema.nodes.bulletList.create({}, listItems)
  }

  private createOrderedList(items: MarkdownNode[]): any {
    const listItems = items.map(item => this.createListItem(item))
    return this.editor.schema.nodes.orderedList.create({}, listItems)
  }

  private createListItem(item: MarkdownNode): any {
    const content = item.content || ''
    const inlineNodes = this.parseAndCreateInlineNodes(content)
    const paragraph = this.editor.schema.nodes.paragraph.create(
      {},
      inlineNodes.length > 0 ? inlineNodes : null
    )
    return this.editor.schema.nodes.listItem.create({}, paragraph)
  }

  private createCodeBlock(content: string): any {
    return this.editor.schema.nodes.codeBlock.create(
      {},
      content ? this.editor.schema.text(content) : null
    )
  }

  private createParagraph(content: string): any {
    // Check if the entire content is just a YouTube URL on its own line
    const trimmedContent = content.trim()
    const youtubeVideoId = this.isYouTubeUrl(trimmedContent)

    // If it's a YouTube URL and we have the YouTube node schema, create a YouTube node
    if (youtubeVideoId && this.editor.schema.nodes.youtube) {
      return this.editor.schema.nodes.youtube.create({
        src: trimmedContent,
        width: 640,
        height: 480
      })
    }

    const lines = content.split('\n')
    const paragraphContent: any[] = []

    for (let i = 0; i < lines.length; i++) {
      if (i > 0) {
        paragraphContent.push(this.editor.schema.nodes.hardBreak.create())
      }

      const inlineNodes = this.parseAndCreateInlineNodes(lines[i])
      paragraphContent.push(...inlineNodes)
    }

    return this.editor.schema.nodes.paragraph.create(
      {},
      paragraphContent.length > 0 ? paragraphContent : null
    )
  }

  // Parse and create inline nodes in one step
  public parseAndCreateInlineNodes(text: string): any[] {
    if (!text) return []
    
    const nodes: any[] = []
    let remaining = text
    
    while (remaining.length > 0) {
      let matched = false
      
      // Code spans (highest precedence)
      const codeMatch = remaining.match(/^`([^`]+)`/)
      if (codeMatch) {
        if (this.editor.schema.marks.code) {
          nodes.push(this.editor.schema.text(codeMatch[1], [
            this.editor.schema.marks.code.create()
          ]))
        } else {
          nodes.push(this.editor.schema.text(codeMatch[1]))
        }
        remaining = remaining.substring(codeMatch[0].length)
        matched = true
        continue
      }
      
      // Triple asterisk (bold + italic)
      const boldItalicMatch = remaining.match(/^\*\*\*(.+?)\*\*\*/)
      if (boldItalicMatch) {
        const marks: any[] = []
        if (this.editor.schema.marks.bold) {
          marks.push(this.editor.schema.marks.bold.create())
        }
        if (this.editor.schema.marks.italic) {
          marks.push(this.editor.schema.marks.italic.create())
        }
        nodes.push(this.editor.schema.text(boldItalicMatch[1], marks))
        remaining = remaining.substring(boldItalicMatch[0].length)
        matched = true
        continue
      }
      
      // Bold
      const boldMatch = remaining.match(/^\*\*(.+?)\*\*/)
      if (boldMatch) {
        if (this.editor.schema.marks.bold) {
          nodes.push(this.editor.schema.text(boldMatch[1], [
            this.editor.schema.marks.bold.create()
          ]))
        } else {
          nodes.push(this.editor.schema.text(boldMatch[1]))
        }
        remaining = remaining.substring(boldMatch[0].length)
        matched = true
        continue
      }
      
      // Italic
      const italicMatch = remaining.match(/^\*([^*]+?)\*/)
      if (italicMatch) {
        if (this.editor.schema.marks.italic) {
          nodes.push(this.editor.schema.text(italicMatch[1], [
            this.editor.schema.marks.italic.create()
          ]))
        } else {
          nodes.push(this.editor.schema.text(italicMatch[1]))
        }
        remaining = remaining.substring(italicMatch[0].length)
        matched = true
        continue
      }
      
      // Strikethrough
      const strikeMatch = remaining.match(/^~~(.+?)~~/)
      if (strikeMatch) {
        if (this.editor.schema.marks.strike) {
          nodes.push(this.editor.schema.text(strikeMatch[1], [
            this.editor.schema.marks.strike.create()
          ]))
        } else {
          nodes.push(this.editor.schema.text(strikeMatch[1]))
        }
        remaining = remaining.substring(strikeMatch[0].length)
        matched = true
        continue
      }
      
      // Link
      const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/)
      if (linkMatch) {
        if (this.editor.schema.marks.link) {
          nodes.push(this.editor.schema.text(linkMatch[1], [
            this.editor.schema.marks.link.create({ href: linkMatch[2] })
          ]))
        } else {
          nodes.push(this.editor.schema.text(linkMatch[1]))
        }
        remaining = remaining.substring(linkMatch[0].length)
        matched = true
        continue
      }
      
      // Plain text (until next markdown pattern or end)
      if (!matched) {
        // Find the next markdown pattern
        const nextPatterns = [
          remaining.indexOf('`'),
          remaining.indexOf('***'),
          remaining.indexOf('**'),
          remaining.indexOf('*'),
          remaining.indexOf('~~'),
          remaining.indexOf('[')
        ].filter(pos => pos > 0)
        
        const nextPos = nextPatterns.length > 0 ? Math.min(...nextPatterns) : remaining.length
        const textContent = remaining.substring(0, nextPos || 1)
        
        // Merge consecutive text nodes
        const lastNode = nodes[nodes.length - 1]
        if (lastNode && lastNode.type === 'text' && (!lastNode.marks || lastNode.marks.length === 0)) {
          lastNode.text += textContent
        } else {
          nodes.push(this.editor.schema.text(textContent))
        }
        
        remaining = remaining.substring(textContent.length)
      }
    }
    
    return nodes
  }

  // Insert nodes into the document
  private insertNodes(tr: any, startPos: number, nodes: any[]): number {
    if (nodes.length === 0) return startPos
    
    // For testing or when nodes are already in the right format
    if (nodes.length > 0 && !nodes[0].toJSON) {
      tr.replaceWith(startPos, startPos, nodes)
      return startPos + nodes.length
    }
    
    // Create a document fragment from nodes
    const fragment = this.editor.schema.nodeFromJSON({
      type: 'doc',
      content: nodes.map(node => node.toJSON())
    }).content
    
    // Replace content at position
    tr.replaceWith(startPos, startPos, fragment)
    return startPos + fragment.size
  }
}